import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Zap, ArrowRight, RefreshCw, CheckCircle2, AlertCircle, Users, Bookmark, Copy } from "lucide-react";
import { CATEGORIES } from "@/lib/constants";
import { useNavigate } from "react-router-dom";

const BRANDS = ["ASOS", "Mango", "Meski", "New Look", "Next", "Zara", "River Island", "Topshop"];

export default function BulkImport() {
    const nav = useNavigate();
    const [urlText, setUrlText] = useState("");
    const [busy, setBusy] = useState(false);
    const [results, setResults] = useState([]); // {url, ok, title, price, image_url, brand, ...}
    const [selected, setSelected] = useState({}); // idx -> bool
    const [defaultCategory, setDefaultCategory] = useState("");
    const [defaultTag, setDefaultTag] = useState("new_arrival");
    const [visibleTo, setVisibleTo] = useState([]);
    const [clients, setClients] = useState([]);
    const [importing, setImporting] = useState(false);
    const [showBookmarklet, setShowBookmarklet] = useState(false);

    // Bookmarklet: grabs all product links on any brand's listing page and copies them to clipboard.
    // Recognises URL patterns used by ASOS (/prd/), Zara (-p), Mango (/p/), Next (-p), New Look (/p/),
    // River Island (/p/) and generic /product/ /style/ paths.
    const BOOKMARKLET = `javascript:(function(){try{var s=new Set();var pats=[/\\/prd\\//,/\\/product\\//,/\\/products\\//,/\\/style\\//,/\\/p\\//,/-p\\d+/,/\\.html/];document.querySelectorAll('a[href]').forEach(function(a){var h=a.href;if(!h||h.indexOf('#')===0)return;for(var i=0;i<pats.length;i++){if(pats[i].test(h)){s.add(h.split('?')[0].split('#')[0]);break;}}});var txt=Array.from(s).join('\\n');var ta=document.createElement('textarea');ta.value=txt;document.body.appendChild(ta);ta.select();document.execCommand('copy');document.body.removeChild(ta);alert('AVCorp: copied '+s.size+' product URLs to clipboard. Paste into Bulk URL Import.');}catch(e){alert('Bookmarklet error: '+e.message);}})();`;

    const copyBookmarklet = async () => {
        try {
            await navigator.clipboard.writeText(BOOKMARKLET);
            toast.success("Bookmarklet copied — paste into a new browser bookmark URL");
        } catch {
            toast.error("Clipboard blocked — long-press the button to copy manually");
        }
    };

    useEffect(() => {
        api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    }, []);

    const urlList = urlText
        .split(/[\n\r]+/)
        .map((u) => u.trim())
        .filter((u) => u.startsWith("http"));

    const fetchAll = async () => {
        if (urlList.length === 0) return toast.error("Paste one or more product URLs, one per line");
        if (urlList.length > 50) return toast.error("Maximum 50 URLs at a time");
        setBusy(true);
        // Seed placeholder rows so the UI shows progress
        const initial = urlList.map((u) => ({ url: u, ok: null, title: "" }));
        setResults(initial);
        setSelected({});
        // Fetch each URL individually with limited concurrency so a single slow site doesn't
        // block the whole batch (bulk-scrape endpoint hit Cloudflare 100s timeout before).
        const CONCURRENCY = 3;
        const PER_URL_TIMEOUT = 90000; // 90 s per URL
        let cursor = 0;
        const runOne = async () => {
            while (cursor < urlList.length) {
                const idx = cursor++;
                const u = urlList[idx];
                try {
                    const r = await api.post("/products/scrape", { url: u }, { timeout: PER_URL_TIMEOUT });
                    const item = { url: u, ok: true, ...r.data };
                    setResults((prev) => { const c = [...prev]; c[idx] = item; return c; });
                    setSelected((prev) => item.title ? { ...prev, [idx]: true } : prev);
                } catch (e) {
                    const msg = e?.response?.data?.detail || e?.message || "fetch failed";
                    setResults((prev) => { const c = [...prev]; c[idx] = { url: u, ok: false, error: String(msg).slice(0, 180) }; return c; });
                }
            }
        };
        const workers = Array.from({ length: Math.min(CONCURRENCY, urlList.length) }, runOne);
        await Promise.all(workers);
        setBusy(false);
        // Toast a summary
        setResults((prev) => {
            const good = prev.filter((x) => x?.ok && x.title).length;
            const bad = prev.filter((x) => x && x.ok === false).length;
            toast.success(`Fetched ${good}/${prev.length}${bad ? ` · ${bad} failed` : ""}`);
            return prev;
        });
    };

    const toggleClient = (id) => {
        setVisibleTo((v) => v.includes(id) ? v.filter((x) => x !== id) : [...v, id]);
    };

    const importSelected = async () => {
        const chosen = results.filter((_, i) => selected[i]);
        if (chosen.length === 0) return toast.error("Tick at least one item to import");
        setImporting(true);
        let ok = 0, fail = 0;
        for (const it of chosen) {
            try {
                await api.post("/products", {
                    title: it.title || "",
                    brand: it.brand || "",
                    price: parseFloat(it.price) || 0,
                    currency: it.currency || "GBP",
                    description: it.description || "",
                    image_url: it.image_url || "",
                    source_url: it.url || "",
                    category: defaultCategory || "",
                    tags: defaultTag ? [defaultTag] : [],
                    visible_to: visibleTo,
                });
                ok++;
            } catch { fail++; }
        }
        setImporting(false);
        toast.success(`Imported ${ok} products${fail ? `, ${fail} failed` : ""}`);
        if (ok > 0) nav("/admin/catalog");
    };

    const setAll = (v) => {
        const s = {};
        results.forEach((_, i) => { s[i] = v && results[i].ok && results[i].title; });
        setSelected(s);
    };

    return (
        <div className="p-4 sm:p-6 lg:p-10 max-w-7xl">
            <div className="mb-8">
                <div className="overline text-neutral-500">Fast Import</div>
                <h1 className="font-display text-5xl tracking-tight leading-none mt-2">Bulk URL Paste</h1>
                <p className="mt-3 text-sm font-body text-neutral-500 max-w-2xl leading-relaxed">
                    Paste up to <b>50 product URLs</b> — one per line — and we&apos;ll fetch title, price, image and description from all of them in parallel. Review, tick the ones you want, hit import.
                </p>
                <div className="mt-4">
                    <button
                        type="button"
                        onClick={() => setShowBookmarklet((v) => !v)}
                        className="inline-flex items-center gap-2 border border-black px-3 py-2 uppercase tracking-widest text-[10px] hover:bg-black hover:text-white transition-colors"
                        data-testid="bookmarklet-toggle-btn"
                    >
                        <Bookmark className="h-3 w-3" strokeWidth={1.5}/>
                        {showBookmarklet ? "Hide" : "Show"} Copy-URLs Bookmarklet
                    </button>
                </div>

                {showBookmarklet && (
                    <div className="mt-4 border border-black bg-neutral-50 p-5 max-w-3xl" data-testid="bookmarklet-panel">
                        <div className="overline text-neutral-500">One-click grabber</div>
                        <h3 className="font-display text-2xl mt-1 mb-3">Copy Product URLs From Any Brand Site</h3>
                        <ol className="text-xs font-body text-neutral-700 space-y-2 list-decimal pl-5 mb-4">
                            <li>Show your browser&apos;s <b>Bookmarks Bar</b> (Cmd/Ctrl+Shift+B).</li>
                            <li><b>Drag</b> the button below onto the bookmarks bar — or copy the code and paste it as a new bookmark&apos;s URL.</li>
                            <li>Open any brand&apos;s new-arrivals listing (ASOS, Mango, Next, Zara…), then <b>click the bookmark</b>.</li>
                            <li>Product URLs are copied to your clipboard. Paste them into the box below.</li>
                        </ol>
                        <div className="flex flex-wrap items-center gap-3">
                            <a
                                href={BOOKMARKLET}
                                onClick={(e) => e.preventDefault()}
                                draggable="true"
                                className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 uppercase tracking-widest text-[10px] cursor-move select-none"
                                data-testid="bookmarklet-drag-link"
                                title="Drag me to your bookmarks bar"
                            >
                                <Bookmark className="h-3 w-3" strokeWidth={1.5}/> Copy AVCorp URLs
                            </a>
                            <button
                                type="button"
                                onClick={copyBookmarklet}
                                className="inline-flex items-center gap-2 border border-black px-4 py-2 uppercase tracking-widest text-[10px] hover:bg-black hover:text-white transition-colors"
                                data-testid="bookmarklet-copy-btn"
                            >
                                <Copy className="h-3 w-3" strokeWidth={1.5}/> Copy Code
                            </button>
                            <span className="text-[10px] font-body text-neutral-500">Tip: Chrome/Safari/Firefox all support drag-to-bookmarks.</span>
                        </div>
                        <details className="mt-4">
                            <summary className="text-[10px] uppercase tracking-widest text-neutral-500 cursor-pointer">View raw script</summary>
                            <pre className="mt-2 p-3 bg-black text-green-300 text-[10px] font-mono overflow-x-auto whitespace-pre-wrap break-all">{BOOKMARKLET}</pre>
                        </details>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                    <Label className="overline text-neutral-600">Product URLs (one per line)</Label>
                    <Textarea
                        value={urlText}
                        onChange={(e) => setUrlText(e.target.value)}
                        placeholder={`https://www.zara.com/uk/en/...\nhttps://www.mango.com/...\nhttps://www.asos.com/...`}
                        className="rounded-none border-black mt-2 font-mono text-xs min-h-[220px]"
                        data-testid="bulk-urls-input"
                    />
                    <div className="text-xs font-body text-neutral-500 mt-2 flex justify-between">
                        <span>{urlList.length} URL{urlList.length === 1 ? "" : "s"} detected</span>
                        <span>Max 50 · Powered by ScraperAPI proxy</span>
                    </div>
                </div>

                {/* Defaults applied to all imported items */}
                <aside className="border border-border p-5 space-y-4">
                    <div className="overline text-neutral-500">Apply to all imports</div>
                    <div>
                        <Label className="overline">Category (optional)</Label>
                        <Select value={defaultCategory || undefined} onValueChange={setDefaultCategory}>
                            <SelectTrigger className="rounded-none border-black mt-1 h-10" data-testid="bulk-category-select">
                                <SelectValue placeholder="None — leave empty"/>
                            </SelectTrigger>
                            <SelectContent>
                                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="overline">Tag</Label>
                        <div className="flex gap-2 mt-2">
                            <button type="button" onClick={() => setDefaultTag(defaultTag === "new_arrival" ? "" : "new_arrival")} className={`px-3 py-1 border uppercase tracking-widest text-[10px] ${defaultTag === "new_arrival" ? "bg-black text-white border-black" : "border-black"}`}>New Arrival</button>
                            <button type="button" onClick={() => setDefaultTag(defaultTag === "best_seller" ? "" : "best_seller")} className={`px-3 py-1 border uppercase tracking-widest text-[10px] ${defaultTag === "best_seller" ? "bg-yellow-400 border-black" : "border-black"}`}>Best Seller</button>
                        </div>
                    </div>
                    <div>
                        <Label className="overline flex items-center gap-2"><Users className="h-3 w-3" strokeWidth={1.5}/> Push to Clients</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                            <button type="button" onClick={() => setVisibleTo([])} className={`px-3 py-1 border uppercase tracking-widest text-[10px] ${visibleTo.length === 0 ? "bg-black text-white border-black" : "border-black"}`}>All</button>
                            {clients.map((c) => {
                                const on = visibleTo.includes(c.id);
                                return (
                                    <button key={c.id} type="button" onClick={() => toggleClient(c.id)}
                                        style={on ? { background: c.brand_color, borderColor: c.brand_color, color: "#fff" } : {}}
                                        className={`px-3 py-1 border uppercase tracking-widest text-[10px] ${on ? "" : "border-black"}`}>
                                        {c.brand_display_name || c.company}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </aside>
            </div>

            <div className="flex gap-3 mt-6">
                <Button
                    onClick={fetchAll}
                    disabled={busy || urlList.length === 0}
                    className="rounded-none h-12 bg-black text-white hover:bg-neutral-800 uppercase tracking-widest text-xs px-6"
                    data-testid="bulk-fetch-btn"
                >
                    {busy ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" strokeWidth={1.5}/> Fetching…</> : <><Zap className="h-4 w-4 mr-2" strokeWidth={1.5}/> Fetch All ({urlList.length})</>}
                </Button>
                {results.length > 0 && (
                    <>
                        <Button variant="outline" onClick={() => setAll(true)} className="rounded-none border-black uppercase tracking-widest text-xs">Select All</Button>
                        <Button variant="outline" onClick={() => setAll(false)} className="rounded-none border-black uppercase tracking-widest text-xs">Deselect</Button>
                        <Button
                            onClick={importSelected}
                            disabled={importing}
                            className="rounded-none h-12 bg-black text-white hover:bg-neutral-800 uppercase tracking-widest text-xs px-6 ml-auto"
                            data-testid="bulk-import-btn"
                        >
                            {importing ? "Importing…" : `Import Selected (${Object.values(selected).filter(Boolean).length})`}
                            <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.5}/>
                        </Button>
                    </>
                )}
            </div>

            {results.length > 0 && (
                <section className="mt-10 border-t border-border pt-6">
                    <div className="overline text-neutral-500 mb-2">Preview · {results.length} items</div>

                    {/* Aggregate error banner — surfaces the dominant failure so it's not hidden in tiny per-card text */}
                    {(() => {
                        const failures = results.filter((r) => r && r.ok === false && r.error);
                        if (!failures.length) return null;
                        const listingCount = failures.filter((r) => /listing page|category/i.test(r.error)).length;
                        const blockedCount = failures.filter((r) => /blocks scrapers|took too long/i.test(r.error)).length;
                        return (
                            <div className="border-2 border-orange-500 bg-orange-50 p-4 mb-4" data-testid="bulk-failure-banner">
                                <div className="uppercase tracking-widest text-[11px] font-bold text-orange-800">
                                    {failures.length} URL{failures.length === 1 ? "" : "s"} failed
                                </div>
                                {listingCount > 0 && (
                                    <div className="mt-2 text-xs font-body text-orange-900">
                                        <b>{listingCount}</b> {listingCount === 1 ? "is" : "are"} <b>category / listing pages</b>, not single products. On the brand site click a product image and copy the URL that appears (it should end in a numeric SKU like <span className="font-mono">/prd/210814590</span> or <span className="font-mono">-p05643803.html</span>).
                                    </div>
                                )}
                                {blockedCount > 0 && (
                                    <div className="mt-2 text-xs font-body text-orange-900">
                                        <b>{blockedCount}</b> site{blockedCount === 1 ? "" : "s"} block{blockedCount === 1 ? "s" : ""} our scraper (Next, New Look, Topshop, Meski). Add these products via <b>Add Product</b> — the form pre-fills title and brand from the URL, you only add price.
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {results.map((r, idx) => {
                            const ok = r.ok && r.title;
                            return (
                                <div key={idx} className={`border p-3 ${ok ? (selected[idx] ? "border-black bg-neutral-50" : "border-border") : "border-red-400 bg-red-50/60"}`} data-testid={`bulk-result-${idx}`}>
                                    <div className="flex items-start gap-2">
                                        <Checkbox
                                            checked={!!selected[idx]}
                                            disabled={!ok}
                                            onCheckedChange={(v) => setSelected((s) => ({ ...s, [idx]: !!v }))}
                                            className="mt-1"
                                            data-testid={`bulk-check-${idx}`}
                                        />
                                        <div className="flex-1 min-w-0">
                                            {ok ? <CheckCircle2 className="h-3 w-3 text-green-700 inline mr-1" strokeWidth={1.5}/> : <AlertCircle className="h-3 w-3 text-red-700 inline mr-1" strokeWidth={1.5}/>}
                                            <span className="overline text-neutral-500 truncate">{r.brand || (ok ? "?" : "Failed")}</span>
                                        </div>
                                    </div>
                                    {ok && (
                                        <div className="aspect-[3/4] bg-neutral-100 my-2 overflow-hidden">
                                            {r.image_url ? (
                                                <img src={r.image_url} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.style.display = 'none'; }}/>
                                            ) : null}
                                        </div>
                                    )}
                                    {ok ? (
                                        <>
                                            <div className="font-body text-xs line-clamp-2 min-h-[2.5em]">{r.title}</div>
                                            <div className="mt-1 flex items-center justify-between">
                                                <span className="font-mono text-xs tabular-nums">{r.price ? `£${Number(r.price).toFixed(2)}` : "—"}</span>
                                                {r.brand && <Badge variant="outline" className="rounded-none text-[8px] uppercase tracking-widest border-black">{r.brand}</Badge>}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="mt-2 text-[11px] font-body text-red-800 leading-snug" data-testid={`bulk-error-${idx}`}>
                                            <div className="uppercase tracking-widest text-[9px] font-bold mb-1">Why it failed</div>
                                            {r.error || "Unknown error"}
                                        </div>
                                    )}
                                    <a href={r.url} target="_blank" rel="noreferrer" className="text-[9px] text-neutral-400 hover:text-black break-all mt-2 block">{r.url}</a>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}
        </div>
    );
}
